import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FolderKanban,
  Kanban,
  FileText,
  Calendar,
  CalendarDays,
  List,
  Settings,
  UserCog,
  Shield,
  FileType,
  LogOut,
  ChevronDown,
  ChevronRight,
  Sun,
  Moon,
  Menu,
  X
} from 'lucide-react';

const menuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard'
  },
  {
    id: 'personnel-relations',
    label: 'Personnel Relations',
    icon: Users,
    submenu: [
      { id: 'new-personnel', label: 'New Personnel', icon: UserPlus, path: '/personnel/new' },
      { id: 'categories', label: 'Category Management', icon: FolderKanban, path: '/categories' },
      { id: 'tracking', label: 'Personnel Tracking', icon: Kanban, path: '/tracking' },
      { id: 'documents', label: 'Document Upload', icon: FileText, path: '/documents' }
    ]
  },
  {
    id: 'hr-operations',
    label: 'Personnel Tracking',
    icon: CalendarDays,
    submenu: [
      { id: 'timesheet', label: 'Timesheet (Puantaj)', icon: Calendar, path: '/timesheet' },
      { id: 'leaves', label: 'Leave Tracking', icon: CalendarDays, path: '/leaves' },
      { id: 'personnel-list', label: 'Personnel List', icon: List, path: '/personnel' }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    submenu: [
      { id: 'personnel-edit', label: 'Personnel Editing', icon: UserCog, path: '/settings/personnel' },
      { id: 'user-management', label: 'User Management', icon: Shield, path: '/settings/users' },
      { id: 'doc-management', label: 'Document Management', icon: FileType, path: '/settings/documents' }
    ]
  }
];

export const MainLayout = ({ children }) => {
  const { user, signout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState(['personnel-relations', 'hr-operations', 'settings']);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleMenu = (menuId) => {
    setOpenMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (submenu) => submenu?.some(item => location.pathname === item.path);

  const handleSignout = () => {
    signout();
    navigate('/signin');
  };

  const NavItem = ({ item, isSubmenuItem = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`
          sidebar-item
          ${isSubmenuItem ? 'pl-12 text-sm' : ''}
          ${active ? 'active' : ''}
        `}
        data-testid={`nav-${item.id}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {sidebarOpen && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Users className="w-6 h-6 text-primary-foreground" />
          </div>
          {sidebarOpen && (
            <div>
              <h1 className="font-bold text-lg tracking-tight">HRMS</h1>
              <p className="text-xs text-muted-foreground">Management System</p>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {menuItems.map((item) => (
            <div key={item.id}>
              {item.submenu ? (
                <Collapsible
                  open={openMenus.includes(item.id)}
                  onOpenChange={() => toggleMenu(item.id)}
                >
                  <CollapsibleTrigger
                    className={`
                      w-full sidebar-item justify-between
                      ${isParentActive(item.submenu) ? 'text-primary' : ''}
                    `}
                    data-testid={`nav-${item.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {sidebarOpen && <span>{item.label}</span>}
                    </div>
                    {sidebarOpen && (
                      openMenus.includes(item.id)
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="slide-in">
                    {item.submenu.map((subItem) => (
                      <NavItem key={subItem.id} item={subItem} isSubmenuItem />
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <NavItem item={item} />
              )}
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="p-4 border-t border-border space-y-3">
        <div className="flex items-center gap-3 px-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {user?.username?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.username}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="flex-shrink-0"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          
          {sidebarOpen && (
            <Button
              variant="ghost"
              className="flex-1 justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignout}
              data-testid="signout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <h1 className="font-bold">HRMS</h1>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="mobile-theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`
          lg:hidden fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50
          transform transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4"
          onClick={() => setMobileOpen(false)}
          data-testid="close-mobile-menu"
        >
          <X className="w-5 h-5" />
        </Button>
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex fixed top-0 left-0 h-full bg-card border-r border-border z-30
          flex-col transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64' : 'w-20'}
        `}
      >
        <SidebarContent />
        
        {/* Collapse button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-20 w-6 h-6 rounded-full border border-border bg-card shadow-sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="collapse-sidebar"
        >
          {sidebarOpen ? <ChevronRight className="w-3 h-3" /> : <ChevronRight className="w-3 h-3 rotate-180" />}
        </Button>
      </aside>

      {/* Main content */}
      <main
        className={`
          transition-all duration-300 ease-in-out
          pt-16 lg:pt-0
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
